import React from 'react';
import { CheckCircle2, Loader2, Save, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDateNL } from '@/lib/format';

export const REVIEW_REASON_LABELS = {
  employee_not_local: 'Medewerker niet gekoppeld aan deze locatie',
  negative_duration: 'Negatieve duur (klok-conflict)',
  overlap_with_open_record: 'Overlap met open registratie',
  te_vroeg_in: 'Te vroeg ingeklokt',
  te_vroeg_uit: 'Te vroeg uitgeklokt',
  vergeten_uitklok: 'Vergeten uit te klokken',
};

/**
 * Shift editing dialog used in Uren Overzicht.
 * Controlled via props — parent owns the open state and form values.
 */
export function EditShiftDialog({
  editShift, selectedDateIso, editForm, setEditForm,
  editSaving, onClose, onSubmit, onResolve,
}) {
  return (
    <Dialog open={!!editShift} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent data-testid="edit-shift-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Shift bewerken</DialogTitle>
          <DialogDescription>
            {editShift?.name} · #{editShift?.personeelsnummer} · {(editShift?.date || selectedDateIso) && formatDateNL(editShift?.date || selectedDateIso)}
          </DialogDescription>
        </DialogHeader>
        {editShift && (
          <div className="space-y-3 py-2">
            {editShift.time_record_needs_review && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="edit-shift-review-reason">
                <p className="font-medium">
                  Review nodig: {REVIEW_REASON_LABELS[editShift.review_reason] || 'Klok-afwijking'}
                </p>
              </div>
            )}
            {(editShift.actual_clock_in || editShift.actual_clock_out) && (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <p className="font-medium text-slate-700">Werkelijk ingeklokt</p>
                <p className="mt-0.5 tabular-nums">
                  {editShift.actual_clock_in || '—'} → {editShift.actual_clock_out || 'open'}
                </p>
                <p className="mt-1 text-[11px] text-slate-500">
                  Tip: vul deze tijden in om gepland gelijk te maken aan werkelijk.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-start" className="text-xs">Begintijd (HH:MM)</Label>
                <Input
                  id="edit-start"
                  data-testid="edit-shift-start"
                  type="time"
                  value={editForm.start_time}
                  onChange={(e) => setEditForm((f) => ({ ...f, start_time: e.target.value }))}
                  className="tabular-nums"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-end" className="text-xs">Eindtijd (HH:MM)</Label>
                <Input
                  id="edit-end"
                  data-testid="edit-shift-end"
                  type="time"
                  value={editForm.end_time}
                  onChange={(e) => setEditForm((f) => ({ ...f, end_time: e.target.value }))}
                  className="tabular-nums"
                />
              </div>
            </div>
            {editShift.actual_clock_in && editShift.actual_clock_out && (
              <button
                type="button"
                data-testid="copy-actual-to-planned"
                onClick={() => setEditForm((f) => ({
                  ...f,
                  start_time: editShift.actual_clock_in,
                  end_time: editShift.actual_clock_out,
                }))}
                className="text-xs text-brand-navy hover:underline"
              >
                ↪ Werkelijke tijden overnemen
              </button>
            )}
            {editShift.time_record_id && (
              <div className="space-y-1.5 rounded-md border border-slate-200 p-3">
                <p className="text-xs font-medium text-slate-700">Werkelijke kloktijden corrigeren</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-actual-in" className="text-xs">Werkelijk ingeklokt</Label>
                    <Input
                      id="edit-actual-in"
                      data-testid="edit-actual-clock-in"
                      type="time"
                      value={editForm.actual_clock_in || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, actual_clock_in: e.target.value }))}
                      className="tabular-nums"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-actual-out" className="text-xs">Werkelijk uitgeklokt</Label>
                    <Input
                      id="edit-actual-out"
                      data-testid="edit-actual-clock-out"
                      type="time"
                      value={editForm.actual_clock_out || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, actual_clock_out: e.target.value }))}
                      className="tabular-nums"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {editShift?.time_record_needs_review && onResolve && (
            <Button
              type="button"
              variant="outline"
              data-testid="edit-shift-resolve"
              onClick={onResolve}
              disabled={editSaving}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 mr-auto"
            >
              <CheckCircle2 size={14} className="mr-1" /> Afgehandeld
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            data-testid="edit-shift-cancel"
            onClick={onClose}
            disabled={editSaving}
          >
            <X size={14} className="mr-1" /> Annuleren
          </Button>
          <Button
            type="button"
            data-testid="edit-shift-save"
            onClick={onSubmit}
            disabled={editSaving}
            className="bg-brand-navy hover:bg-brand-navy/90 text-white"
          >
            {editSaving ? (
              <Loader2 size={14} className="mr-1 animate-spin" />
            ) : (
              <Save size={14} className="mr-1" />
            )}
            Opslaan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

